import { useCallback, useEffect, useState } from 'react';
import { isApiError } from '@aiptyg/types';
import * as settingsService from '../services/settings.service';
import type {
  AppleAuthConfigDTO,
  AppleAuthConfigUpdateParams,
} from '../types/settings.types';
import { useToastStore } from '../../../store/toast.store';

const EMPTY_FORM: AppleAuthConfigUpdateParams = {
  auth_endpoint: '',
  email_domain_restriction_enabled: true,
  allowed_email_domains: ['@icloud.com', '@qq.com', '@163.com', '@126.com', '@sina.com', '@sina.cn', '@vip.sina.com'],
};

function resolveError(e: unknown): string {
  if (isApiError(e)) {
    const map: Record<string, string> = {
      admin_role_required: '无管理员权限',
      network_error: '网络连接失败，请检查网络',
      'invalid apple auth config': 'Apple 登录接口配置无效，请填写 HTTPS URL',
    };
    return map[e.message] ?? `请求失败 (${e.message})`;
  }
  return '未知错误';
}

function dtoToForm(dto: AppleAuthConfigDTO): AppleAuthConfigUpdateParams {
  return {
    auth_endpoint: dto.auth_endpoint ?? '',
    email_domain_restriction_enabled: dto.email_domain_restriction_enabled ?? true,
    allowed_email_domains: Array.isArray(dto.allowed_email_domains)
      ? dto.allowed_email_domains
      : EMPTY_FORM.allowed_email_domains,
  };
}

function isValidEndpoint(value: string): boolean {
  try {
    const parsed = new URL(value.trim());
    return parsed.protocol === 'https:' && parsed.hostname.length > 0 && parsed.username === '' && parsed.password === '';
  } catch {
    return false;
  }
}

function normalizeEmailDomains(domains: string[]): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const domain of domains) {
    const item = domain.trim().toLowerCase();
    if (!item || seen.has(item)) continue;
    seen.add(item);
    normalized.push(item);
  }
  return normalized;
}

function isValidEmailDomain(value: string): boolean {
  return /^@[a-z0-9](?:[a-z0-9-]*[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]*[a-z0-9])?)+$/.test(value);
}

export function useAppleAuthConfig() {
  const addToast = useToastStore((s) => s.addToast);
  const [data, setData] = useState<AppleAuthConfigDTO | null>(null);
  const [form, setForm] = useState<AppleAuthConfigUpdateParams>(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await settingsService.getAppleAuthConfig();
      setData(result);
      setForm(dtoToForm(result));
    } catch (e) {
      setError(resolveError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateField = useCallback(<K extends keyof AppleAuthConfigUpdateParams>(
    key: K,
    value: AppleAuthConfigUpdateParams[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const save = useCallback(async () => {
    const endpoint = form.auth_endpoint.trim();
    if (!isValidEndpoint(endpoint)) {
      addToast('error', '请填写有效的 HTTPS Apple 登录接口地址');
      return false;
    }
    const allowedEmailDomains = normalizeEmailDomains(form.allowed_email_domains);
    if (form.email_domain_restriction_enabled && allowedEmailDomains.length === 0) {
      addToast('error', '开启邮箱限制时至少需要配置一个允许的邮箱后缀');
      return false;
    }
    if (allowedEmailDomains.some((domain) => !isValidEmailDomain(domain))) {
      addToast('error', '邮箱后缀必须以 @ 开头，例如 @icloud.com');
      return false;
    }

    setSaving(true);
    try {
      const result = await settingsService.updateAppleAuthConfig({
        auth_endpoint: endpoint,
        email_domain_restriction_enabled: form.email_domain_restriction_enabled,
        allowed_email_domains: allowedEmailDomains,
      });
      setData(result);
      setForm(dtoToForm(result));
      addToast('success', 'Apple ID 验证配置已保存，App 下一次验证会读取新规则');
      return true;
    } catch (e) {
      addToast('error', resolveError(e));
      return false;
    } finally {
      setSaving(false);
    }
  }, [addToast, form]);

  return {
    data,
    form,
    loading,
    saving,
    error,
    updateField,
    save,
    reload: load,
  };
}
